import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, Warning, ArrowRight, Confetti } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";

const ICONS = {
  done: <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />,
  attention: <Warning size={18} weight="fill" className="text-amber-500 shrink-0" />,
  todo: <Circle size={18} className="text-slate-300 shrink-0" />,
};

export function CCGuide({ periodKey, full = false, refreshKey = 0 }) {
  const [g, setG] = useState(null);
  useEffect(() => {
    if (!periodKey) { setG(null); return; }
    api.get(`/commission/guide?period_key=${periodKey}`).then((r) => setG(r.data)).catch(() => setG(null));
  }, [periodKey, refreshKey]);
  if (!g) return null;

  const cur = g.steps.find((s) => s.key === g.current);
  const idx = g.steps.findIndex((s) => s.key === g.current);

  if (!full) {
    if (g.all_done || !cur) return null;
    return (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-4 py-3 mb-5 flex items-center gap-3 flex-wrap" data-testid="cc-guide-banner">
        <div className="flex items-center gap-2 text-indigo-700 text-xs font-semibold uppercase tracking-wide shrink-0">
          <ArrowRight size={16} weight="bold" /> Do this next · step {idx + 1} of {g.total}
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-medium text-sm text-slate-900">{cur.title}{cur.count ? ` (${cur.count})` : ""}.</span>{" "}
          <span className="text-sm text-slate-600">{cur.plain}</span>
        </div>
        <Link to={cur.link} className="shrink-0">
          <Button size="sm" data-testid="cc-guide-banner-btn">{cur.button}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white mb-6 overflow-hidden" data-testid="cc-guide-checklist">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-medium text-sm">Your monthly checklist — {periodKey}</h2>
        <span className="text-xs text-slate-500">{g.done} of {g.total} done</span>
      </div>
      {g.all_done && (
        <div className="px-4 py-3 bg-emerald-50 text-emerald-800 text-sm flex items-center gap-2" data-testid="cc-guide-all-done">
          <Confetti size={18} weight="fill" /> All done for {periodKey} — commissions are posted and payroll is out.
        </div>
      )}
      <ol>
        {g.steps.map((s, i) => {
          const isCur = s.key === g.current;
          return (
            <li key={s.key} data-testid={`cc-guide-step-${s.key}`}
              className={`flex items-start gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 ${isCur ? "bg-indigo-50/60" : ""}`}>
              <span className="mt-0.5">{ICONS[s.status]}</span>
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${s.status === "done" ? "text-slate-400 line-through decoration-slate-300" : "font-medium text-slate-900"}`}>
                  {i + 1}. {s.title}{s.count ? <span className={`ml-1 text-xs font-semibold ${s.status === "done" ? "text-slate-400" : "text-amber-700"}`}>({s.count})</span> : null}
                  {isCur && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">← you are here</span>}
                </p>
                {s.status !== "done" && <p className="text-xs text-slate-500 mt-0.5">{s.plain}</p>}
              </div>
              {s.status !== "done" && (
                <Link to={s.link} className="shrink-0">
                  <Button size="sm" variant={isCur ? "default" : "outline"} className="h-7 text-xs" data-testid={`cc-guide-btn-${s.key}`}>
                    {s.button}
                  </Button>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
